import { io, Socket } from 'socket.io-client';
import { Message, TypingIndicator } from '../types';
import { normalizeMessage } from '../utils/normalizeMessage';
import { activeWorkspaceStore } from '@/shared/workspace/activeWorkspaceStore';

type SocketEventHandler<T = any> = (data: T) => void;

/**
 * WebSocket service for real-time messaging
 * 
 * Implements FR-077 through FR-081:
 * - Real-time message delivery in active conversations
 * - Typing indicators
 * - Read receipts
 * - Connection management with token authentication
 * 
 * Usage:
 * 1. Call connect() with JWT token when user opens messaging
 * 2. Register event handlers (onNewMessage, onTypingIndicator, etc.)
 * 3. Send messages via sendMessage()
 * 4. Call disconnect() when leaving messaging
 */
class SocketService {
  private socket: Socket | null = null;
  private eventHandlers: Map<string, Set<SocketEventHandler>> = new Map();
  private isConnecting: boolean = false;
  private lastToken: string | null = null;

  constructor() {
    // Feature 075: when a SUB_DELEGATE switches workspace, the gateway must
    // re-resolve the workspace context — reconnect with the new handshake
    // payload. No-op while disconnected or for non-delegates (store unset).
    activeWorkspaceStore.subscribe(() => {
      if (this.socket && this.lastToken) {
        const token = this.lastToken;
        this.disconnectSocketOnly();
        this.connect(token);
      }
    });
  }

  /**
   * Connect to WebSocket server
   * 
   * @param token - JWT authentication token
   */
  connect(token: string): void {
    // Prevent multiple simultaneous connection attempts
    if (this.socket?.connected || this.isConnecting) {
      return;
    }

    this.isConnecting = true;
    this.lastToken = token;
    const wsUrl = import.meta.env.VITE_WS_URL || '';

    // Feature 075: the gateway resolves the delegated workspace from
    // `auth.workspaceOwnerId` (P5-T14/T15). Absent for non-delegates.
    const workspaceOwnerId = activeWorkspaceStore.get() ?? undefined;

    this.socket = io(`${wsUrl}/messages`, {
      auth: { token, workspaceOwnerId },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    });

    // Setup event listeners
    this.socket.on('connect', () => {
      this.isConnecting = false;
    });

    this.socket.on('disconnect', () => {
      this.isConnecting = false;
    });

    this.socket.on('error', (error: any) => {
      console.error('WebSocket error:', error);
    });

    // Setup message event handlers
    this.socket.on('newMessage', (message: Message) => {
      this.emit('newMessage', normalizeMessage(message as any));
    });

    this.socket.on('messageUpdate', (message: Message) => {
      this.emit('messageUpdate', normalizeMessage(message as any));
    });

    this.socket.on('messagesRead', (data: { conversationId: string; readAt: Date }) => {
      this.emit('messagesRead', data);
    });

    this.socket.on('typingIndicator', (data: TypingIndicator) => {
      this.emit('typingIndicator', data);
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnecting = false;
      this.lastToken = null;
      this.eventHandlers.clear();
    }
  }

  /**
   * Tear down the socket while KEEPING registered event handlers — used by
   * the workspace-switch reconnect so feature components don't lose their
   * subscriptions mid-session.
   */
  private disconnectSocketOnly(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnecting = false;
    }
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Send a message via WebSocket
   * 
   * @param recipientId - Recipient user ID
   * @param content - Message content
   * @returns Promise resolving to created message
   */
  sendMessage(recipientId: string, content: string): Promise<Message> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Socket not connected'));
        return;
      }

      this.socket.emit('sendMessage', { recipientId, content }, (response: Message) => {
        resolve(normalizeMessage(response as any));
      });
    });
  }

  /**
   * Mark conversation as read
   * 
   * @param conversationId - Conversation ID
   */
  markAsRead(conversationId: string): void {
    if (!this.socket?.connected) {
      console.warn('Socket not connected, cannot mark as read');
      return;
    }

    this.socket.emit('markAsRead', { conversationId });
  }

  /**
   * Send typing indicator
   * 
   * @param recipientId - Recipient user ID
   * @param isTyping - Whether user is typing
   */
  sendTypingIndicator(recipientId: string, isTyping: boolean): void {
    if (!this.socket?.connected) {
      return;
    }

    this.socket.emit('typingIndicator', { recipientId, isTyping });
  }

  /**
   * Register event handler
   * 
   * @param event - Event name
   * @param handler - Event handler function
   */
  on<T = any>(event: string, handler: SocketEventHandler<T>): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }

  /**
   * Unregister event handler
   * 
   * @param event - Event name
   * @param handler - Event handler function to remove
   */
  off(event: string, handler: SocketEventHandler): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Emit event to registered handlers
   * 
   * @param event - Event name
   * @param data - Event data
   */
  private emit(event: string, data: any): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in ${event} handler:`, error);
        }
      });
    }
  }
}

// Export singleton instance
export const socketService = new SocketService();

